/**
 * Exports a DOM subtree to a PDF: an html2canvas screenshot for the visual, plus an
 * invisible text and link layer so the output stays selectable, searchable and clickable.
 */

import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { addTextLayer } from "./textLayer";

interface Margin {
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

interface Options {
  filename: string;
  margin?: number | Margin;
  textLayer?: boolean;
  scale?: number;
}

const DEFAULT_SCALE = 2;
// Gates loading of images inside the capture, the report header logo above all.
const IMAGE_TIMEOUT = 5000;

const normalizeMargin = (margin: number | Margin) =>
  typeof margin === "number"
    ? { left: margin, right: margin, top: margin, bottom: margin }
    : { left: 0, right: 0, top: 0, bottom: 0, ...margin };

export const exportDomToPdf = async (
  root: HTMLElement,
  { filename, margin = 0, textLayer = true, scale = DEFAULT_SCALE }: Options
) => {
  const canvas = await html2canvas(root, {
    backgroundColor: "#ffffff", // html2canvas defaults to transparent
    scale, // fixed, so the output does not vary with devicePixelRatio
    imageTimeout: IMAGE_TIMEOUT,
    logging: false
  });

  if (!canvas.width || !canvas.height) {
    throw new Error("PDF export failed: the captured canvas is empty");
  }

  const { left, right, top, bottom } = normalizeMargin(margin);
  // CSS px are used as PDF points 1:1, which keeps the text layer coordinates trivial.
  const contentWidth = canvas.width / scale;
  const contentHeight = canvas.height / scale;
  const width = contentWidth + left + right;
  const height = contentHeight + top + bottom;

  // A single content-sized page, matching kendo's default paperSize: "auto".
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({
    unit: "pt",
    orientation: width > height ? "landscape" : "portrait",
    format: [width, height]
  });

  pdf.addImage(canvas, "JPEG", left, top, contentWidth, contentHeight);

  if (textLayer) {
    await addTextLayer(pdf, root, { left, top });
  }

  pdf.save(filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
};
